import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PLATE_READS_MAX, type PlateDetection, type PlateSighting } from '@iot-dashboard/shared';
import { usePlates } from '../../api/hooks';
import { DataTable, type Column } from '../../components/DataTable';
import { Tabs } from '../../components/Form';
import { Panel } from '../../components/Panel';
import { PlateThumbnail } from '../../components/PlateThumbnail';
import { StatRow, StatTile } from '../../components/StatTile';
import { ErrorState, Loading } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';
import { compactNumber, formatDateTime } from '../../lib/format';
import { vehicleSearch } from '../../lib/plates';
import { useDebounced } from '../../lib/useDebounced';
import { KnownBadge, PlateReadDialog, VehiclePlate } from './PlateReadDialog';

const RANGES = [
  { key: '24h', label: 'Last 24 hours', hours: 24 },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7 },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30 },
  { key: 'all', label: 'Everything kept', hours: 0 },
] as const;

/** Rows drawn at a time; drawing all 2,000 reads at once takes seconds on a phone. */
const ROWS_PER_PAGE = 200;
/** A search reads through the whole time range, so it waits for typing to pause. */
const SEARCH_DELAY_MS = 300;

export function PlatesPage() {
  const { tenantId, base, canConfigure } = useTenantContext();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<'reads' | 'vehicles'>('reads');
  // By id rather than position: a refresh that brings in a new read must not
  // move the dialog onto a different vehicle.
  const [viewing, setViewing] = useState<{ id: string; editPlate: boolean } | null>(null);

  const rangeKey = (params.get('range') as (typeof RANGES)[number]['key'] | null) ?? '7d';
  const hours = RANGES.find((r) => r.key === rangeKey)?.hours ?? 24 * 7;
  const plate = params.get('plate') ?? '';
  const search = useDebounced(plate.trim(), SEARCH_DELAY_MS);
  const deviceId = params.get('camera') ?? '';

  const from = useMemo(() => {
    if (hours === 0) return undefined;
    const now = new Date();
    now.setSeconds(0, 0);
    return new Date(now.getTime() - hours * 3_600_000).toISOString();
  }, [hours]);

  const plates = usePlates(tenantId, {
    ...(search ? { plate: search } : {}),
    ...(deviceId ? { deviceId } : {}),
    ...(from ? { from } : {}),
    limit: PLATE_READS_MAX,
  });

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const reads = plates.data?.items ?? [];
  const vehicles = plates.data?.vehicles ?? [];
  // Reads come newest first, so the first read of each plate is its newest.
  const newestRead = useMemo(() => {
    const byPlate = new Map<string, PlateDetection>();
    for (const r of reads) if (!byPlate.has(r.plate)) byPlate.set(r.plate, r);
    return byPlate;
  }, [reads]);
  const cameras = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of reads) if (r.deviceId) seen.set(r.deviceId, r.deviceName ?? r.deviceId);
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [reads]);

  const showVehicle = (v: Parameters<typeof vehicleSearch>[0]) => setParam('plate', vehicleSearch(v));
  // Only a vehicle Protect sends by name has a plate to add, and only administrators add it.
  const addPlate = (read: PlateDetection | undefined) =>
    canConfigure && read ? () => setViewing({ id: read.detectionId, editPlate: true }) : undefined;

  const readColumns: Column<PlateDetection>[] = [
    {
      key: 'plate',
      header: 'Plate',
      primary: true,
      sortValue: (d) => d.plate,
      render: (d) => (
        <span className="plate-cell">
          <PlateThumbnail tenantId={tenantId} read={d} onOpen={() => setViewing({ id: d.detectionId, editPlate: false })} />
          <VehiclePlate vehicle={d} onShow={() => showVehicle(d)} onAddPlate={addPlate(d)} />
        </span>
      ),
    },
    { key: 'seen', header: 'Seen', sortValue: (d) => d.ts, render: (d) => <span title={formatDateTime(d.ts)}><TimeAgo iso={d.ts} /></span> },
    {
      key: 'camera',
      header: 'Camera',
      sortValue: (d) => d.deviceName ?? '',
      render: (d) =>
        d.deviceId ? (
          <Link to={`${base}/cameras/${encodeURIComponent(d.deviceId)}`} onClick={(e) => e.stopPropagation()}>
            {d.deviceName ?? d.deviceId}
          </Link>
        ) : (
          <span className="muted">Unknown camera</span>
        ),
    },
    { key: 'known', header: 'Vehicle', sortValue: (d) => String(d.known), render: (d) => <KnownBadge known={d.known} /> },
    { key: 'alarm', header: 'Alarm', render: (d) => <span className="small">{d.alarmName ?? '—'}</span> },
  ];

  const vehicleColumns: Column<PlateSighting>[] = [
    {
      key: 'plate',
      header: 'Plate',
      primary: true,
      sortValue: (v) => v.plate,
      render: (v) => <VehiclePlate vehicle={v} onShow={() => showVehicle(v)} onAddPlate={addPlate(newestRead.get(v.plate))} />,
    },
    { key: 'count', header: 'Reads', align: 'right', sortValue: (v) => v.count, render: (v) => v.count },
    { key: 'last', header: 'Last seen', sortValue: (v) => v.lastSeen, render: (v) => <span title={formatDateTime(v.lastSeen)}><TimeAgo iso={v.lastSeen} /></span> },
    { key: 'first', header: 'First seen', sortValue: (v) => v.firstSeen, render: (v) => <span title={formatDateTime(v.firstSeen)}><TimeAgo iso={v.firstSeen} /></span> },
    { key: 'cameras', header: 'Cameras', render: (v) => <span className="small">{v.cameras.join(', ') || '—'}</span> },
  ];

  if (plates.isError) return <ErrorState error={plates.error} retry={() => plates.refetch()} />;

  const notOnAList = reads.filter((r) => r.known === false).length;
  const filtered = Boolean(plate || deviceId || rangeKey !== '7d');
  const viewingIndex = viewing ? reads.findIndex((r) => r.detectionId === viewing.id) : -1;
  // When the range holds more reads than one request returns, the counts and
  // list cover only the newest. Every range ends now, so the banner points to
  // a camera or a search, which reach past them.
  const more = plates.data?.more ?? false;
  // A search reads the whole range; one too long to read in time says how far it got.
  const searchedFrom = plates.data?.searchedFrom;
  const max = compactNumber(PLATE_READS_MAX);
  const tooMany = search
    ? `More than ${max} reads match this search in this time range.`
    : deviceId
      ? `This camera has more than ${max} reads in this time range.`
      : `This time range has more than ${max} reads.`;
  const narrower = search
    ? ' Type more of the plate or name to narrow it down.'
    : deviceId
      ? " Search for a plate to see one vehicle's older visits."
      : ' Pick a camera or search for a plate to see older reads.';

  return (
    <>
      <PageHeader
        title="Licence plates"
        subtitle="Plates read by this tenant's cameras, newest first. Reads arrive from the consoles as they happen."
      />

      {searchedFrom ? (
        <div className="alert-banner alert-banner--info">
          <div className="grow">
            <strong>Searched back to {formatDateTime(searchedFrom)}.</strong> This time range holds too many reads to
            search them all for part of a plate at once, so older reads were only checked for the whole plate or name
            typed.{deviceId ? '' : ' Pick a camera to search further back.'}
          </div>
        </div>
      ) : more ? (
        <div className="alert-banner alert-banner--info">
          <div className="grow">
            <strong>{tooMany}</strong> The counts and list below cover only the newest {max}.{narrower}
          </div>
        </div>
      ) : null}

      <StatRow>
        <StatTile label="Reads" value={`${compactNumber(reads.length)}${more || searchedFrom ? '+' : ''}`} hint={RANGES.find((r) => r.key === rangeKey)?.label.toLowerCase()} />
        <StatTile label="Vehicles" value={compactNumber(vehicles.length)} hint="distinct vehicles" tone="accent" />
        <StatTile label="Not on a list" value={compactNumber(notOnAList)} tone={notOnAList ? 'warning' : 'good'} hint="unrecognised vehicles" />
      </StatRow>

      <Panel
        flush
        title={
          <div className="filters">
            <input
              className="input"
              placeholder="Search plates or names"
              value={plate}
              onChange={(e) => setParam('plate', e.target.value)}
              aria-label="Search by part of a plate, or of a known vehicle's name"
            />
            <select className="select" value={deviceId} onChange={(e) => setParam('camera', e.target.value)} aria-label="Filter by camera">
              <option value="">All cameras</option>
              {cameras.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
            <select className="select" value={rangeKey} onChange={(e) => setParam('range', e.target.value)} aria-label="Time range">
              {RANGES.map((r) => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>
            {filtered ? (
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setParams({}, { replace: true })}>
                Clear filters
              </button>
            ) : null}
          </div>
        }
        actions={
          <Tabs
            tabs={[
              { key: 'reads', label: 'Reads', count: reads.length },
              { key: 'vehicles', label: 'Vehicles', count: vehicles.length },
            ]}
            active={tab}
            onChange={setTab}
          />
        }
      >
        {plates.isPending ? (
          <Loading label="Loading plate reads…" />
        ) : tab === 'reads' ? (
          // Keyed so each tab's table starts on its own sort and first page,
          // rather than taking over the other tab's.
          <DataTable
            key="reads"
            columns={readColumns}
            rows={reads}
            rowKey={(d) => d.detectionId}
            defaultSort={{ key: 'seen', dir: 'desc' }}
            refetching={plates.isFetching}
            empty={<EmptyPlates base={base} filtered={filtered} />}
            pageSize={ROWS_PER_PAGE}
          />
        ) : (
          <DataTable
            key="vehicles"
            columns={vehicleColumns}
            rows={vehicles}
            rowKey={(v) => v.plate}
            defaultSort={{ key: 'last', dir: 'desc' }}
            refetching={plates.isFetching}
            empty={<EmptyPlates base={base} filtered={filtered} />}
            pageSize={ROWS_PER_PAGE}
          />
        )}
      </Panel>

      {viewingIndex >= 0 ? (
        <PlateReadDialog
          tenantId={tenantId}
          base={base}
          reads={reads}
          index={viewingIndex}
          canConfigure={canConfigure}
          editPlate={viewing?.editPlate}
          onNavigate={(r) => setViewing({ id: r.detectionId, editPlate: false })}
          onShowVehicle={(r) => {
            setViewing(null);
            setTab('reads');
            showVehicle(r);
          }}
          onClose={() => setViewing(null)}
        />
      ) : null}
    </>
  );
}

/**
 * Nothing here usually means the alarm was never created, not that no vehicle
 * drove past, so the empty state says what to go and do.
 */
function EmptyPlates({ base, filtered }: { base: string; filtered: boolean }) {
  if (filtered) {
    return (
      <div className="empty">
        <h3>No plates match</h3>
        <p>Try a wider time range, or clear the filters.</p>
      </div>
    );
  }
  return (
    <div className="empty">
      <h3>No plates have been read yet</h3>
      <p>
        The console pushes plate reads to this dashboard rather than the dashboard polling for them, so a
        camera that detects licence plates also needs an Alarm Manager rule pointed at this tenant's
        webhook address.
      </p>
      <p>
        <Link to={`${base}/integrations`} className="btn btn--sm">Open the integration page</Link>
      </p>
    </div>
  );
}
